import React, { useCallback, useRef, useState } from 'react';
import { makeClassName } from '@siteless/util';

type SliderProps = {
  min?: number;
  max?: number;
  step?: number;
  value: number;
  onChange: (value: number) => void;
  className?: string;
  trackClassName?: string;
  thumbClassName?: string;
};

const Slider = ({
  min = 0,
  max = 100,
  step = 1,
  value,
  onChange,
  className,
  trackClassName,
  thumbClassName,
}: SliderProps) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const percent = ((value - min) / (max - min)) * 100;

  const handlePointerEvent = useCallback(
    (clientX: number) => {
      const track = trackRef.current;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const raw = min + ratio * (max - min);
      const stepped = Math.round(raw / step) * step;
      const clamped = Math.max(min, Math.min(max, stepped));
      onChange(clamped);
    },
    [min, max, step, onChange],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      setIsDragging(true);
      handlePointerEvent(e.clientX);
    },
    [handlePointerEvent],
  );

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (e.buttons === 0) return;
      handlePointerEvent(e.clientX);
    },
    [handlePointerEvent],
  );

  return (
    <div
      className={makeClassName(
        'relative flex h-8 w-full cursor-pointer touch-none select-none items-center',
        className,
      )}
      ref={trackRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <div
        className={makeClassName(
          'relative h-3 w-full overflow-hidden rounded-full border-2 border-neutral-200 bg-neutral-100',
          trackClassName,
        )}
      >
        <div
          className="absolute left-0 top-0 h-full rounded-full bg-rose-default"
          style={{ width: `${percent}%` }}
        />
      </div>
      <div
        className={makeClassName(
          'pointer-events-none absolute top-1/2 h-6 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-rose-dark transition-shadow',
          isDragging && 'ring-4 ring-rose-bright/20',
          thumbClassName,
        )}
        style={{ left: `${percent}%` }}
      />
    </div>
  );
};

export default Slider;
